import { Injectable } from '@angular/core';
import { ApiService } from './api.service';
import { RawPermissions } from '../models/raw-permissions.model';
import { Permissions } from '../models/permissions.model';
import { BehaviorSubject, Subscription } from 'rxjs';
import { SessionStorageService } from './session-storage.service';
import { OAuthUserInfo } from '../models/oauthuserinfo.model';
import { permissionsSchema } from 'src/utilities/constants/permissions.schema';
import { AppConfig } from 'src/app/app-config';
import { Router } from '@angular/router';

@Injectable({
  providedIn: 'root'
})
export class PermissionService {

  // raw list of database permissions that we use to determine functionality
  private rawPermissions: Array<RawPermissions>;

  // POJO of permissions that equate to visible/hidden UI fields
  private permissions: Permissions;

  private getPermissions = new BehaviorSubject<Permissions>(null);
  public permissions$ = this.getPermissions.asObservable();

  private getRawPermsissions = new BehaviorSubject<Array<RawPermissions>>(null);
  public rawPermissions$ = this.getRawPermsissions.asObservable();

  private getCompanyContext = new BehaviorSubject<string>(null);
  public companyContext$ = this.getCompanyContext.asObservable();

  private userInfosSubscription: Subscription;
  private userSub: string;

  constructor(private router: Router, private apiService: ApiService, private sessionStorage: SessionStorageService, private appConfig: AppConfig) {
    this.userInfosSubscription = this.sessionStorage.userInfosChange.subscribe( (data: string) => {
      let userInfo: OAuthUserInfo = <OAuthUserInfo>JSON.parse(data);

      if (userInfo) {
        this.userSub = userInfo.sub;
      }
    });
  }

  // make API call to get permissions
  fetchPermissions() {
    if(!this.userSub) return;

    return this.apiService.get(`/users/${this.userSub}/permissions`).then( (permissions: Array<RawPermissions>) => {
      const userinfos = this.sessionStorage.getUserInfos();
      const companies = userinfos.arvCompanyID;
      if (companies ) {
        if ( companies.length === 1 && Array.isArray(companies)) {
          permissions = permissions.filter(perm => perm.arvCompanyId === companies[0])
        } else if (companies.length > 1 && Array.isArray(companies)) {
          permissions = permissions.filter(perm => companies.includes(perm.arvCompanyId))
        } else {
          permissions = permissions.filter(perm => perm.arvCompanyId === companies)
        }
      } else {
        permissions = null
      }

      this.setPermissions(permissions);
    });
  }

  // set permissions in memory
  setPermissions(permissionData: Array<RawPermissions>, index?: number) {
    let promise = new Promise((resolve, reject) => {
      this.rawPermissions = permissionData;
      // console.log("Permissions: ", permissionData);
      this.getRawPermsissions.next(this.rawPermissions);
      let i = index || 0;
      let permissions = <Permissions>{};

      if (!this.rawPermissions) {
        this.router.navigateByUrl('access-denied')

        reject('Invalid permissions')
      }
      let validPermissions = this.validatePermissions(this.rawPermissions[i]);
      if(!validPermissions) {
        this.router.navigateByUrl('access-denied')

        reject('Invalid permissions')
      };


      if (this.rawPermissions[i] === null || this.rawPermissions[i] === undefined) {
        permissions.canViewLoans = false;
        return;
      }

      if (this.rawPermissions[i]) {
        if (this.appConfig.config.displayEpicTwoFeatures) {
          if (this.appConfig.config.displayAdminFeatures.displayAdminLandingPage) {
            permissions.canViewAdmin = this.determineViewAdmin(this.rawPermissions[i]);
            permissions.canEditUser = this.appConfig.config.displayAdminFeatures.displayCreateEditUser;
          }
          permissions.canViewReports = this.determineViewReports(this.rawPermissions[i]);
          permissions.canViewDocuments = this.determineViewDocuments(this.rawPermissions[i]);
          if (this.appConfig.config.displayMyProfile) {
            permissions.canViewProfile = this.determineViewProfile(this.rawPermissions[i]);
          }
          permissions.canCRUDMasterAdmin = this.determineCRUDMasterAdmin(this.rawPermissions[i]);
        }
  
        permissions.roleName = this.rawPermissions[i].roleName;
        permissions.canViewLoans = this.determineViewLoans(this.rawPermissions[i]);
        permissions.canViewComments = this.determineViewComments(this.rawPermissions[i]);
      } else {
        permissions.canViewLoans = false;
      }



      this.sessionStorage.setCompany(this.rawPermissions[i].companyName);
      this.getPermissions.next(permissions);
      this.permissions = permissions;

      resolve();
    });

    return promise;
  }

  // check to see if we have permissions loaded
  // if not, load them
  checkPermissions() {

    if (this.permissions) {
      console.log("Already have permissions, shortcircuit");
      return;
    }

    this.fetchPermissions();
  }

  // make sure the permissions match the model and have good values
  validatePermissions(arg: RawPermissions): boolean {
    // check to make sure we have all the available keys
    // and values are within acceptable ranges
    let isValid:boolean = !!Object
      .keys(permissionsSchema)
      .filter(key => !permissionsSchema[key](this.rawPermissions[key]));

    if (!arg) {
      isValid = false;
    }
    console.log(`Validation check is ${isValid}`)

    return isValid;
  }

  determineViewAdmin(arg: RawPermissions) {
    return arg.viewAdminNavCode > 1;
  }

  determineViewReports(arg: RawPermissions) {
    return arg.viewReportsCode > 1;
  }

  determineViewDocuments(arg: RawPermissions) {
    return arg.viewDocumentsCode > 1;
  }

  determineViewProfile(arg: RawPermissions) {
    return arg.viewOtherCode > 1;
  }

  determineViewLoans(arg: RawPermissions) {
    const userinfos = this.sessionStorage.getUserInfos();
    const companies = userinfos.arvCompanyID;
    if ( companies.length === 1 && Array.isArray(companies)) {
      if (userinfos.arvCompanyID !== arg.arvCompanyId && userinfos.arvCompanyID !== arg.arvCompanyId) {
        console.log("DB and Arv company mismatch")
        return false;
      } else {
        return arg.viewOtherCode > 1;
      }
    } else if ( companies.length > 1 && Array.isArray(companies) ) {
      let flag = false;
      companies.forEach(company => {
        if (company === arg.arvCompanyId) {
          flag = true
        }
      })
      return flag;
    } else {
      if (companies === arg.arvCompanyId) {
        return true;
      } else {
        return false;
      }
    }

    
  }

  determineViewComments(arg: RawPermissions) {
    return arg.viewCommentsCode > 1;
  }

  determineCRUDMasterAdmin(arg: RawPermissions) {
    return arg.crudMasterAdminCode > 1;
  }

}
